import { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { supabase } from "@/integrations/supabase/client";
import { DEACTIVATED_MESSAGE, isDeactivated } from "@/lib/deactivated";
import AppShellSkeleton from "@/components/layout/AppShellSkeleton";
import type { AccountStatus } from "@/hooks/use-account-status";

interface Gate {
  status: AccountStatus;
  /** The brand has not finished onboarding. */
  incomplete: boolean;
}

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { session, loading } = useAuth();
  const location = useLocation();
  const userId = session?.user.id;

  // One cached answer for the whole session, instead of three requests in a row on every navigation.
  // The onboarding page invalidates it when the brand finishes, so a new brand is not sent back.
  const { data: gate, isPending } = useQuery({
    queryKey: ["route-gate", userId],
    enabled: !loading && !!userId,
    staleTime: 2 * 60 * 1000,
    queryFn: async (): Promise<Gate> => {
      const [profileRes, memberRes] = await Promise.all([
        supabase.from("profiles").select("status").eq("id", userId!).maybeSingle(),
        supabase.from("team_members").select("brand_id").eq("user_id", userId!).limit(1).maybeSingle(),
      ]);
      const status = ((profileRes.data?.status as AccountStatus | undefined) ?? "pending") as AccountStatus;
      // A rejected account is deactivated, wherever it signed in from (including Google).
      if (isDeactivated(status)) return { status, incomplete: false };

      const brandId = memberRes.data?.brand_id;
      if (!brandId) return { status, incomplete: true };
      const { data: brand } = await supabase.from("brands").select("profile_completed_at").eq("id", brandId).maybeSingle();
      return { status, incomplete: !brand?.profile_completed_at };
    },
  });

  const deactivated = isDeactivated(gate?.status);
  useEffect(() => {
    if (deactivated) void supabase.auth.signOut();
  }, [deactivated]);

  // The onboarding page is always accessible (everyone, including pending).
  const onOnboarding = location.pathname === "/onboarding";

  if (loading || (session && isPending)) {
    return onOnboarding ? <div className="min-h-screen bg-background" /> : <AppShellSkeleton />;
  }

  // Redirect first, then sign out (the effect above), so the message is not lost when the session disappears.
  if (deactivated) {
    return <Navigate to="/login" replace state={{ authMessage: DEACTIVATED_MESSAGE }} />;
  }

  if (!session) {
    return <Navigate to="/login" replace />;
  }

  if (gate?.incomplete && !onOnboarding) {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
